"use client";

import { usePathname, useSearchParams } from "next/navigation";
import React, { useEffect, Suspense } from "react";
import { usePostHog } from "posthog-js/react";

import { ErrorBoundary } from './bugsnag'
import Grid2 from '@mui/material/Grid2'
import Typography from '@mui/material/Typography';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { isPostHogActive } from "../utils";
import {registerServiceWorker} from "../utils/registerClearSW";

export function BugSnagProvider({ children }) {

    const ErrorView = () => (
        <Grid2 container direction="column" alignItems="center" justifyContent="center" style={{ minHeight: '80vh' }}>
            <ErrorOutlineOutlined sx={{ fontSize: 40, color: 'gray' }} />
            <Typography variant="h5" color="textSecondary">
                Oops! Something went wrong on our end!
            </Typography>
            <small>We track these errors automatically. If the problem persists, contact us.</small>
        </Grid2>
    )

    return <>
        {ErrorBoundary &&
            <ErrorBoundary FallbackComponent={ErrorView}>
                {children}
            </ErrorBoundary>
        }
        {!ErrorBoundary && children}
    </>
}

export function PostHogProvider({ children }) {
    useEffect(() => {

        registerServiceWorker();

        if (isPostHogActive()) {
            if (typeof window !== 'undefined') {
                posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
                    api_host: "/ingest",
                    person_profiles: "identified_only", // or 'always' to create profiles for anonymous users as well
                    capture_pageview: false, // Disable automatic pageview capture, as we capture manually
                    capture_pageleave: true,
                    autocapture: false,
                });
            }
        }
    }, []);

    return (
        <PHProvider client={posthog}>
            <SuspendedPostHogPageView />
            {children}
        </PHProvider>
    );
}

function PostHogPageView() {
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const posthog = usePostHog();

    // Track pageviews
    useEffect(() => {
        if (pathname && posthog) {
            let url = window.origin + pathname;
            if (searchParams.toString()) {
                url = url + "?" + searchParams.toString();
            }

            if (isPostHogActive()) {
                posthog.capture("$pageview", { $current_url: url });
            }
        }
    }, [pathname, searchParams, posthog]);

    return null;
}

// Wrap PostHogPageView in Suspense to avoid the useSearchParams usage above
// from de-opting the whole app into client-side rendering
// See: https://nextjs.org/docs/messages/deopted-into-client-rendering
function SuspendedPostHogPageView() {
    return (
        <Suspense fallback={""}>
            <PostHogPageView />
        </Suspense>
    );
}
